<template lang="pug">
.send-message-targets-input
  //- families
  .register-row(v-show="value == 'families'")
    .register-group
      .register-key
        span.key-name 宛先
        span.validation-label 必須
      .register-value.flex-wrap.send-targets
        .loading(v-if="isFamiliesBusy")
          b-spinner.spinner
          span 宛先読込中...
        button.btn.send-target-select-btn.mr-2(v-else @click="onFamiliesSelect") 宛先選択
        template(v-for="group in familiesTargets")
          template(v-for="resident in group.residents")
            .send-target.d-flex(v-for="family in resident.families" v-if="family.isTarget")
              span.target-name.align-self-center {{ family.familyName }}{{ getSenderType(family.senderType) }}
              b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(family)") X
      .error-messages(v-if="errors.has('families-targets')")
        div(v-for="error in errors.collect('families-targets')")
          label.error-message {{ error }}
  //- otherUserGroups
  .register-row(v-show="value == 'otherUsers'")
    .register-group
      .register-key
        span.key-name 宛先
        span.validation-label 必須
      .register-value.flex-wrap.send-targets
        .loading(v-if="isOtherUserBusy")
          b-spinner.spinner
          span 宛先読込中...
        button.btn.send-target-select-btn.mr-2(v-else @click="onOtherUserGroupsSelect") 宛先選択
        template(v-for="target in otherUserTargets")
          .send-target.d-flex(v-for="member in target.members" v-if="member.isTarget")
            span.target-name.align-self-center {{ member.memberName }}{{ getSenderType(member.senderType) }}
            b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(member)") X
      .error-messages(v-if="errors.has('other-user-groups-targets')")
        div(v-for="error in errors.collect('other-user-groups-targets')")
          label.error-message {{ error }}
  //- group
  .register-row(v-show="value == 'groups'")
    .register-group
      .register-key
        span.key-name 宛先
        span.validation-label 必須
      .register-value.flex-wrap.send-targets
        .loading(v-if="isGroupBusy")
          b-spinner.spinner
          span 宛先読込中...
        button.btn.send-target-select-btn.mr-2(v-else @click="onSendMessageGroupSelect") 宛先選択
        .send-target.d-flex(v-for="group in groupTargets.familyGroups" v-if="group.isTarget")
          span.target-name.align-self-center {{ group.name }}
          b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(group)") X
        .send-target.d-flex(v-for="group in groupTargets.otherUserGroupMemberGroups" v-if="group.isTarget")
          span.target-name.align-self-center {{ group.name }}
          b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(group)") X
        .send-target.d-flex(v-for="group in groupTargets.residentGroups" v-if="group.isTarget")
          span.target-name.align-self-center {{ group.name }}
          b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(group)") X
        .send-target.d-flex(v-for="group in groupTargets.otherUserGroups" v-if="group.isTarget")
          span.target-name.align-self-center {{ group.name }}
          b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(group)") X
      .error-messages(v-if="errors.has('send-message-group-targets')")
        div(v-for="error in errors.collect('send-message-group-targets')")
          label.error-message {{ error }}
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import { userStore } from "@/store/store";

import SystemError from "@/commons/models/SystemError";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import { SendMessageTargetGroup, SendMessageTargetResident, SendTargetFamily } from "@/commons/models/SendMessageTarget";

import FamiliesService, { FamilyGetModel, SenderType } from "@/commons/services/Families";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import OtherUserGroupTypeMembersService, { OtherUserGroupTypeGroup, OtherUserGroupTypeMember } from "@/commons/services/OtherUserGroupTypeMembers";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import SendMessageGroupsService, { SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import SendMessageGroupMembersService, {
  SendMessageGroupMembersFamilyGetModel,
  SendMessageGroupMembersOtherUserGroupMemberGetModel
} from "@/commons/services/SendMessageGroupMembers";

import { OtherUserGroupInfo, ResidentGroupInfo, SendMessageGroupInfo } from "@/commons/services/Questionnaires";
import OtherUserGroup from "@/commons/models/OtherUserGroup";
import { GroupType } from "@/pc/components/modals/SendMessageGroupSelectModal.vue";
import { SendMessageTarget } from "./SendMessageTargetCheck.vue";

/**
 * ### メッセージ送信宛先選択コンポーネント
 */
@Component({})
export default class SendMessageTargetsInput extends Vue {
  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 家族サービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループメンバー取得サービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** 施設関係者グループ種類にひもづくメンバー取得サービス */
  @Inject("OtherUserGroupTypeMembersService") otherUserGroupTypeMembersService!: OtherUserGroupTypeMembersService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 宛先グループメンバーサービス */
  @Inject("SendMessageGroupMembersService") sendMessageGroupMembersService!: SendMessageGroupMembersService;

  /** 宛先種別 */
  @Prop()
  value!: SendMessageTarget;
  /** グループを選択した場合のグループ種別 */
  @Prop()
  groupType!: GroupType;

  /** ページ全体ローディング表示可否 */
  isLoading: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 宛先(家族) */
  familiesTargets: Array<SendMessageTargetGroup> = [];
  /** 宛先読込み中 */
  isFamiliesBusy: boolean = true;
  /** 宛先(施設関係者) */
  otherUserTargets: Array<OtherUserGroupTypeGroup> = [];
  /** 宛先読込み中(施設関係者) */
  isOtherUserBusy: boolean = true;
  /** 宛先(グループ) */
  groupTargets: GroupModel = { familyGroups: [], otherUserGroupMemberGroups: [], residentGroups: [], otherUserGroups: [] };
  /** 宛先読込み中(グループ) */
  isGroupBusy: boolean = true;
  /** 家族全員に送信で使用する、利用者グループフィルタされていない全家族 */
  allFamilies: Array<FamilyGetModel> = [];
  /** 施設関係者メンバー */
  otherUserGroupMembersInFacility: Array<OtherUserGroup> = [];

  /**
   *  vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  public async createView(): Promise<void> {
    this.isFamiliesBusy = true;
    this.isOtherUserBusy = true;
    this.isGroupBusy = true;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const familiesTargets: Array<SendMessageTargetGroup> = await this.createFamiliesTargets(residentGroups);
    this.familiesTargets = familiesTargets;
    const allFamilies: Array<FamilyGetModel> = await this.getNonmaskedFamilies();
    this.allFamilies = allFamilies;
    this.isFamiliesBusy = false;

    this.$emit("loadedFamiliesTargets");

    const otherUserTargets: Array<OtherUserGroupTypeGroup> = await this.createOtherUserTargets();
    this.otherUserTargets = otherUserTargets;
    this.isOtherUserBusy = false;

    this.$emit("loadedOtherUserTargets");

    const sendMessageGroups: Array<SendMessageGroupsGetModel> = await this.sendMessageGroupsService.gets(userStore);
    const otherUserGroups: Array<OtherUserGroup> = await this.otherUserGroupsService.gets(userStore);
    const otherUserGroupMembersInFacility: Array<OtherUserGroup> = await this.otherUserGroupMembersService.get(userStore);
    this.otherUserGroupMembersInFacility = otherUserGroupMembersInFacility;
    const groupTargets: GroupModel = await this.createGroupTargets(sendMessageGroups, residentGroups, allFamilies, otherUserGroups, otherUserGroupMembersInFacility);
    this.groupTargets = groupTargets;
    this.isGroupBusy = false;

    this.$emit("loadedGroupTargets");

    return;
  }

  public restoreFamilyTargets(targets: string[]): void {
    let map: Map<string, string> = new Map<string, string>();
    for (const targetSortKey of targets) {
      map.set(targetSortKey, targetSortKey);
    }
    for (const targetGroup of this.familiesTargets) {
      for (const targetResident of targetGroup.residents) {
        for (let targetFamily of targetResident.families) {
          const sortKey: string | undefined = map.get(targetFamily.residentsSortKey);
          if (sortKey != undefined) {
            targetFamily.isTarget = true;
          } else {
            targetFamily.isTarget = false;
          }
        }
      }
    }
    return;
  }

  public restoreOtherUserTargets(targets: string[]): void {
    let map: Map<string, string> = new Map<string, string>();
    for (const targetSortKey of targets) {
      map.set(targetSortKey, targetSortKey);
    }
    for (let group of this.otherUserTargets) {
      for (let member of group.members) {
        const sortKey: string | undefined = map.get(member.otherUserGroupsSortKey);
        if (sortKey != undefined) {
          member.isTarget = true;
        } else {
          member.isTarget = false;
        }
      }
    }
    return;
  }

  public restoreGroupTargets(
    sendMessageGroupInfos: Array<SendMessageGroupInfo>,
    residentGroupInfos: Array<ResidentGroupInfo> | undefined,
    otherUserGroupInfos: Array<OtherUserGroupInfo> | undefined
  ): void {
    // 宛先グループ（家族、施設関係者）
    const sendMessageGroupMap: Map<string, SendMessageGroupInfo> = new Map<string, SendMessageGroupInfo>();
    for (const groupInfo of sendMessageGroupInfos) {
      sendMessageGroupMap.set(groupInfo.sendMessageGroupsSortKey, groupInfo);
    }
    for (let group of this.groupTargets.familyGroups) {
      const groupInfo: SendMessageGroupInfo | undefined = sendMessageGroupMap.get(group.sendMessageGroupsSortKey);
      if (groupInfo != undefined) {
        group.isTarget = true;
      } else {
        group.isTarget = false;
      }
    }
    for (let group of this.groupTargets.otherUserGroupMemberGroups) {
      const groupInfo: SendMessageGroupInfo | undefined = sendMessageGroupMap.get(group.sendMessageGroupsSortKey);
      if (groupInfo != undefined) {
        group.isTarget = true;
      } else {
        group.isTarget = false;
      }
    }
    // 利用者グループ
    // 過去のデータには情報が存在しないので、その場合は処理不要
    if (residentGroupInfos) {
      const residentGroupMap: Map<number, ResidentGroupInfo> = new Map<number, ResidentGroupInfo>();
      for (const groupInfo of residentGroupInfos) {
        residentGroupMap.set(groupInfo.id, groupInfo);
      }
      for (let group of this.groupTargets.residentGroups) {
        const groupInfo: ResidentGroupInfo | undefined = residentGroupMap.get(group.id);
        if (groupInfo != undefined) {
          group.isTarget = true;
        } else {
          group.isTarget = false;
        }
      }
    }
    // 施設関係者グループ
    // 過去のデータには情報が存在しないので、その場合は処理不要
    if (otherUserGroupInfos) {
      const otherUserGroupMap: Map<number, OtherUserGroupInfo> = new Map<number, OtherUserGroupInfo>();
      for (const groupInfo of otherUserGroupInfos) {
        otherUserGroupMap.set(groupInfo.id, groupInfo);
      }
      for (let group of this.groupTargets.otherUserGroups) {
        const groupInfo: OtherUserGroupInfo | undefined = otherUserGroupMap.get(group.id);
        if (groupInfo != undefined) {
          group.isTarget = true;
        } else {
          group.isTarget = false;
        }
      }
    }
    return;
  }

  /**
   * ### 家族個別の宛先対象リストを作成する
   *
   * - 利用者-家族でネストして画面表示するため、構成を合わせる
   * - APIから家族を取得すると、家族にひもづく利用者の情報もついてくるので、家族を取得するだけで情報は作れる
   */
  public async createFamiliesTargets(residentGroups: Array<ResidentGroupsGetModel>): Promise<Array<SendMessageTargetGroup>> {
    // 宛先対象の元となる家族を取得する
    // 0件なら宛先対象は空配列を返す
    const families: Array<FamilyGetModel> = await this.getMaskedFamilies();
    if (families.length == 0) {
      return [];
    }

    let targetResidents: Array<SendMessageTargetResident> = [];
    let residentId: number = families[0].id ? families[0].id : -1;
    let targetResident: SendMessageTargetResident = this.createTargetResident(families[0]);

    for (let i = 1; i < families.length; i++) {
      const family: FamilyGetModel = families[i];

      // 利用者が変わったら新しい利用者を作って、ひもづく家族を追加する
      if (residentId != family.id) {
        targetResidents.push(targetResident);
        targetResident = this.createTargetResident(family);
        residentId = family.id;
      }
      // 利用者が変わらなければ利用者に家族を追加する
      else {
        const targetFamily = this.createTargetFamily(family);
        targetResident.families.push(targetFamily);
      }
    }
    // 最終の利用者を追加する
    targetResidents.push(targetResident);

    // モーダルに表示するのは利用者グループ－利用者－家族の階層構造なので利用者グループに利用者をひもづける
    let targets: Array<SendMessageTargetGroup> = [];
    for (const group of residentGroups) {
      let residents: Array<SendMessageTargetResident> = [];
      for (const resident of targetResidents) {
        if (resident.groupId == group.id) {
          residents.push(resident);
        }
      }
      targets.push({
        id: group.id,
        name: group.name,
        show: true,
        residents: residents
      });
    }

    return targets;
  }

  /**
   * ### 宛先で選択候補とする家族を取得する
   */
  public async getMaskedFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      response = await this.familiesService.getsInFacility(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }

    return response;
  }

  /**
   * ### 送信対象の利用者を作成する
   */
  public createTargetResident(family: FamilyGetModel): SendMessageTargetResident {
    const target: SendMessageTargetResident = {
      id: family.id,
      birthday: family.birthday,
      facilityId: family.facilityId,
      gender: family.gender,
      password: family.password,
      name: family.name,
      kanaName: family.kanaName,
      tel: family.tel,
      room: family.room,
      groupId: family.groupId,
      show: true,
      families: []
    };
    target.families.push(this.createTargetFamily(family));

    return target;
  }

  /**
   * ### 送信対象の家族を作成する
   */
  public createTargetFamily(family: FamilyGetModel): SendTargetFamily {
    const target: SendTargetFamily = {
      residentsSortKey: family.residentsSortKey,
      familyId: family.familyId ? family.familyId : 0,
      familyName: family.familyName ? family.familyName : "",
      familyTel: family.familyTel ? family.familyTel : "",
      familyLineId: family.familyLineId ? family.familyLineId : "",
      familyBirthDay: family.familyBirthDay ? family.familyBirthDay : 0,
      familyGender: family.familyGender ? family.familyGender : "",
      familyRelation: family.familyRelation ? family.familyRelation : "",
      familyPosition: family.familyPosition ? family.familyPosition : "",
      familyMailaddress: family.familyMailaddress ? family.familyMailaddress : "",
      senderType: family.senderType,
      show: true,
      isTarget: false
    };

    return target;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 宛先で選択候補とする家族を取得する
   */
  public async getNonmaskedFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      const params: any = {
        masking: "none"
      };
      response = await this.familiesService.getsInFacility(userStore, params);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300,
            masking: "none"
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }

    return response;
  }

  /**
   * ### 施設関係者個別の宛先対象リストを作成する
   *
   * - グループ-メンバーでネストして画面表示するため、構成を合わせる
   */
  public async createOtherUserTargets(): Promise<Array<OtherUserGroupTypeGroup>> {
    // 宛先対象の元となる施設関係者メンバーを取得する
    // 0件なら宛先対象は空配列を返す
    const groups: Array<OtherUserGroupTypeGroup> = await this.getOtherUserGroupMembers();
    if (groups.length == 0) {
      return [];
    }

    // グループ種類名を格納する
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherGroupTypes();
    const otherUserGroupTypesMap: Map<number, string> = this.createOtherUserGroupTypesMap(otherUserGroupTypes);
    for (const group of groups) {
      const groupTypeName: string | undefined = otherUserGroupTypesMap.get(group.groupTypeId);
      group.groupName = groupTypeName ? groupTypeName : "";
      group.show = true;
    }

    return groups;
  }

  /**
   * ### グループ-メンバーを取得する
   */
  public async getOtherUserGroupMembers(): Promise<Array<OtherUserGroupTypeGroup>> {
    const targets: Array<OtherUserGroupTypeGroup> = await this.otherUserGroupTypeMembersService.get(userStore, -1);
    for (const target of targets) {
      for (let member of target.members as Array<OtherUserGroupTypeMember>) {
        member.isTarget = false;
        member.show = true;
      }
    }

    return targets;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);
    return otherUserGroupTypes;
  }

  /**
   * ### 画面表示用に施設関係者グループ種類のMAPを作成する
   */
  public createOtherUserGroupTypesMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let map: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      map.set(type.id, type.name);
    }
    return map;
  }

  /**
   * ### 宛先グループの選択ダイアログ情報を作成する
   */
  public async createGroupTargets(
    sendMessageGroups: Array<SendMessageGroupsGetModel>,
    residentGroups: Array<ResidentGroupsGetModel>,
    allFamilies: Array<FamilyGetModel>,
    otherUserGroups: Array<OtherUserGroup>,
    otherUserGroupMembersInFacility: Array<OtherUserGroup>
  ): Promise<GroupModel> {
    const sendMessageGroupModel: GroupModel = {
      familyGroups: [],
      otherUserGroupMemberGroups: [],
      residentGroups: [],
      otherUserGroups: []
    };
    // 宛先グループの情報作成
    for (const group of sendMessageGroups) {
      const _group: SendMessageGroupTargetModel = Object.assign({}, group) as SendMessageGroupTargetModel;
      _group.isTarget = false;
      _group.memberCount = group.members.length;
      if (_group.type == GroupType.FAMILY) {
        sendMessageGroupModel.familyGroups.push({
          ..._group,
          show: true
        });
      } else if (_group.type == GroupType.OTHER_USER) {
        sendMessageGroupModel.otherUserGroupMemberGroups.push({
          ..._group,
          show: true
        });
      }
    }
    // 利用者グループの情報を作成
    // 利用者グループは人数をTBLに持っていないので、MAPを作って集計する
    const residentGroupMemberCountMap = new Map<number, number>();
    for (const family of allFamilies) {
      let memberCount: number | undefined = residentGroupMemberCountMap.get(family.groupId);
      if (memberCount) {
        memberCount++;
        residentGroupMemberCountMap.set(family.groupId, memberCount);
      } else {
        residentGroupMemberCountMap.set(family.groupId, 1);
      }
    }
    for (const group of residentGroups) {
      const _group: ResidentGroupTargetModel = Object.assign({}, group) as ResidentGroupTargetModel;
      _group.isTarget = false;
      const memberCount: number | undefined = residentGroupMemberCountMap.get(_group.id);
      _group.memberCount = memberCount ? memberCount : 0;
      sendMessageGroupModel.residentGroups.push({
        ..._group,
        key: `residentGroup_${_group.id}`,
        show: true
      });
    }
    // 施設関係者グループの情報を作成
    // 施設関係者グループは人数をTBLに持っていないので、MAPを作って集計する
    const otherUserGroupMemberCountMap = new Map<number, number>();
    for (const member of otherUserGroupMembersInFacility) {
      let memberCount: number | undefined = otherUserGroupMemberCountMap.get(member.id);
      if (memberCount) {
        memberCount++;
        otherUserGroupMemberCountMap.set(member.id, memberCount);
      } else {
        otherUserGroupMemberCountMap.set(member.id, 1);
      }
    }
    for (const group of otherUserGroups) {
      const _group: OtherUserGroupTargetModel = Object.assign({}, group) as OtherUserGroupTargetModel;
      _group.isTarget = false;
      const memberCount: number | undefined = otherUserGroupMemberCountMap.get(_group.id);
      _group.memberCount = memberCount ? memberCount : 0;
      sendMessageGroupModel.otherUserGroups.push({
        ..._group,
        key: `otherUserGroup_${_group.id}`,
        show: true
      });
    }

    return sendMessageGroupModel;
  }

  public getSenderType(senderType: SenderType): string {
    if (senderType == SenderType.MAIL) {
      return "（メール）";
    } else {
      return "";
    }
  }

  public deleteTarget(target: any) {
    target.isTarget = false;
    return;
  }

  public onFamiliesSelect(): void {
    this.$emit("onFamiliesSelect", this.familiesTargets);
    return;
  }

  public onOtherUserGroupsSelect(): void {
    this.$emit("onOtherUserGroupsSelect", this.otherUserTargets);
    return;
  }

  public onSendMessageGroupSelect(): void {
    this.$emit("onSendMessageGroupSelect", this.groupTargets);
    return;
  }

  public async createTargets(): Promise<{ targets: string[]; lineTargets: string[]; mailTargets: string[] }> {
    const targets: string[] = [];
    const lineTargets: string[] = [];
    const mailTargets: string[] = [];

    switch (this.value) {
      case SendMessageTarget.ALL_FAMILIES: {
        for (const family of this.allFamilies) {
          targets.push(family.residentsSortKey);

          if (family.senderType == SenderType.LINE) {
            lineTargets.push(family.residentsSortKey);
          } else if (family.senderType == SenderType.MAIL) {
            mailTargets.push(family.residentsSortKey);
          }
        }
        break;
      }
      case SendMessageTarget.FAMILIES: {
        for (const group of this.familiesTargets) {
          for (const resident of group.residents) {
            for (const family of resident.families) {
              if (family.isTarget == true) {
                targets.push(family.residentsSortKey);

                if (family.senderType == SenderType.LINE) {
                  lineTargets.push(family.residentsSortKey);
                } else if (family.senderType == SenderType.MAIL) {
                  mailTargets.push(family.residentsSortKey);
                }
              }
            }
          }
        }
        break;
      }
      case SendMessageTarget.OTHER_USERS: {
        for (const group of this.otherUserTargets) {
          for (const member of group.members) {
            if (member.isTarget == true) {
              targets.push(member.otherUserGroupsSortKey);

              if (member.senderType == SenderType.LINE) {
                lineTargets.push(member.otherUserGroupsSortKey);
              } else if (member.senderType == SenderType.MAIL) {
                mailTargets.push(member.otherUserGroupsSortKey);
              }
            }
          }
        }
        break;
      }
      case SendMessageTarget.GROUPS: {
        switch (this.groupType) {
          case GroupType.FAMILY: {
            for (const group of this.groupTargets.familyGroups) {
              if (group.isTarget) {
                const families: Array<SendMessageGroupMembersFamilyGetModel> = await this.getSendMessageGroupFamilyMembers(group.id);
                // 送信用のLINEIDまたはメールアドレスを追加していく
                for (const family of families) {
                  if (family.senderType == SenderType.LINE && family.familyLineId) {
                    lineTargets.push(family.residentsSortKey as string);
                  } else if (family.senderType == SenderType.MAIL && family.familyMailaddress) {
                    mailTargets.push(family.residentsSortKey as string);
                  }
                  targets.push(family.residentsSortKey as string);
                }
              }
            }
            break;
          }
          case GroupType.OTHER_USER: {
            for (const group of this.groupTargets.otherUserGroupMemberGroups) {
              if (group.isTarget) {
                const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = await this.getSendMessageGroupOtherGroupUserMembers(group.id);
                // 送信用のLINEIDまたはメールアドレスを追加していく
                for (const member of members) {
                  if (member.senderType == SenderType.LINE && member.memberLineId) {
                    lineTargets.push(member.otherUserGroupsSortKey as string);
                  } else if (member.senderType == SenderType.MAIL && member.memberMailaddress) {
                    mailTargets.push(member.otherUserGroupsSortKey as string);
                  }
                  targets.push(member.otherUserGroupsSortKey);
                }
              }
            }
            break;
          }
          case GroupType.RESIDENT_GROUP: {
            // 利用者グループにひもづく家族をMAPでまとめる
            const residentGroupMemberMap = new Map<number, Array<FamilyGetModel>>();
            for (const family of this.allFamilies) {
              const families: Array<FamilyGetModel> | undefined = residentGroupMemberMap.get(family.groupId);
              if (families) {
                families.push(family);
                residentGroupMemberMap.set(family.groupId, families);
              } else {
                residentGroupMemberMap.set(family.groupId, [family]);
              }
            }

            for (const group of this.groupTargets.residentGroups) {
              if (group.isTarget) {
                // 送信用のLINEIDまたはメールアドレスを追加していく
                const members: Array<FamilyGetModel> | undefined = residentGroupMemberMap.get(group.id);
                if (!members) {
                  throw new SystemError("error residentGroup is not found");
                }
                for (const member of members) {
                  if (member.senderType == SenderType.LINE && member.familyLineId) {
                    lineTargets.push(member.residentsSortKey as string);
                  } else if (member.senderType == SenderType.MAIL && member.familyMailaddress) {
                    mailTargets.push(member.residentsSortKey as string);
                  }
                  targets.push(member.residentsSortKey);
                }
              }
            }
            break;
          }
          case GroupType.OTHER_USER_GROUP: {
            // 施設関係者グループにひもづくメンバーをMAPでまとめる
            const otherUserGroupMemberMap = new Map<number, Array<OtherUserGroup>>();
            for (const member of this.otherUserGroupMembersInFacility) {
              const members: Array<OtherUserGroup> | undefined = otherUserGroupMemberMap.get(member.id);
              if (members) {
                members.push(member);
                otherUserGroupMemberMap.set(member.id, members);
              } else {
                otherUserGroupMemberMap.set(member.id, [member]);
              }
            }

            for (const group of this.groupTargets.otherUserGroups) {
              if (group.isTarget) {
                // 送信用のLINEIDまたはメールアドレスを追加していく
                const members: Array<OtherUserGroup> | undefined = otherUserGroupMemberMap.get(group.id);
                if (!members) {
                  throw new SystemError("error otherUserGroup is not found");
                }
                for (const member of members) {
                  if (member.senderType == SenderType.LINE && member.memberLineId) {
                    lineTargets.push(member.otherUserGroupsSortKey as string);
                  } else if (member.senderType == SenderType.MAIL && member.memberMailaddress) {
                    mailTargets.push(member.otherUserGroupsSortKey as string);
                  }
                  targets.push(member.otherUserGroupsSortKey);
                }
              }
            }
            break;
          }
        }
        break;
      }
    }

    return {
      targets,
      lineTargets,
      mailTargets
    };
  }

  /**
   * ### 宛先グループにひもづく家族ユーザーを取得する
   */
  public async getSendMessageGroupFamilyMembers(groupId: number): Promise<Array<SendMessageGroupMembersFamilyGetModel>> {
    const members: Array<SendMessageGroupMembersFamilyGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersFamilyGetModel>;
    return members;
  }

  /**
   * ### 宛先グループにひもづく施設関係者グループメンバーを取得する
   */
  public async getSendMessageGroupOtherGroupUserMembers(groupId: number): Promise<Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>> {
    const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      groupId
    )) as Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>;
    return members;
  }

  /**
   * ### 宛先グループ情報を作成する
   */
  public createSendMessageGroupInfos(): Array<SendMessageGroupInfo> {
    const groupInfos: Array<SendMessageGroupInfo> = [];
    switch (this.value) {
      case SendMessageTarget.ALL_FAMILIES:
      case SendMessageTarget.FAMILIES:
      case SendMessageTarget.OTHER_USERS: {
        break;
      }
      case SendMessageTarget.GROUPS: {
        switch (this.groupType) {
          case GroupType.FAMILY:
          case GroupType.OTHER_USER: {
            for (let group of this.groupTargets.familyGroups) {
              if (group.isTarget) {
                groupInfos.push({
                  sendMessageGroupsSortKey: group.sendMessageGroupsSortKey,
                  id: group.id,
                  name: group.name,
                  type: group.type as number,
                  memberSortKeys: group.members
                });
              }
            }
            for (let group of this.groupTargets.otherUserGroupMemberGroups) {
              if (group.isTarget) {
                groupInfos.push({
                  sendMessageGroupsSortKey: group.sendMessageGroupsSortKey,
                  id: group.id,
                  name: group.name,
                  type: group.type as number,
                  memberSortKeys: group.members
                });
              }
            }
          }
        }
        break;
      }
    }
    return groupInfos;
  }

  /**
   * ### 利用者グループ情報を作成する
   */
  public createResidentGroupInfos(): Array<ResidentGroupInfo> {
    const groupInfos: Array<ResidentGroupInfo> = [];
    switch (this.value) {
      case SendMessageTarget.ALL_FAMILIES:
      case SendMessageTarget.FAMILIES:
      case SendMessageTarget.OTHER_USERS: {
        break;
      }
      case SendMessageTarget.GROUPS: {
        switch (this.groupType) {
          case GroupType.RESIDENT_GROUP: {
            // 利用者グループにひもづく家族をMAPでまとめる
            const residentGroupMemberMap = new Map<number, string[]>();
            for (const family of this.allFamilies) {
              const residentsSortKeys: string[] | undefined = residentGroupMemberMap.get(family.groupId);
              if (residentsSortKeys) {
                residentsSortKeys.push(family.residentsSortKey);
                residentGroupMemberMap.set(family.groupId, residentsSortKeys);
              } else {
                residentGroupMemberMap.set(family.groupId, [family.residentsSortKey]);
              }
            }
            for (let group of this.groupTargets.residentGroups) {
              if (group.isTarget) {
                const residentsSortKeys: string[] | undefined = residentGroupMemberMap.get(group.id);
                groupInfos.push({
                  id: group.id,
                  name: group.name,
                  residentsSortKeys: residentsSortKeys ? residentsSortKeys : []
                });
              }
            }
          }
        }
        break;
      }
    }
    return groupInfos;
  }

  /**
   * ### 宛先グループ情報を作成する
   */
  public createOtherUserGroupInfos(): Array<OtherUserGroupInfo> {
    const groupInfos: Array<OtherUserGroupInfo> = [];
    switch (this.value) {
      case SendMessageTarget.ALL_FAMILIES:
      case SendMessageTarget.FAMILIES:
      case SendMessageTarget.OTHER_USERS: {
        break;
      }
      case SendMessageTarget.GROUPS: {
        switch (this.groupType) {
          case GroupType.OTHER_USER_GROUP: {
            // 施設関係者グループにひもづくメンバーをMAPでまとめる
            const otherUserGroupMemberMap = new Map<number, string[]>();
            for (const member of this.otherUserGroupMembersInFacility) {
              const memberSortKeys: string[] | undefined = otherUserGroupMemberMap.get(member.id);
              if (memberSortKeys) {
                memberSortKeys.push(member.otherUserGroupsSortKey);
                otherUserGroupMemberMap.set(member.id, memberSortKeys);
              } else {
                otherUserGroupMemberMap.set(member.id, [member.otherUserGroupsSortKey]);
              }
            }
            for (let group of this.groupTargets.otherUserGroups) {
              if (group.isTarget) {
                const memberSortKeys: string[] | undefined = otherUserGroupMemberMap.get(group.id);
                groupInfos.push({
                  otherUserGroupsSortKey: group.otherUserGroupsSortKey,
                  id: group.id,
                  name: group.name,
                  memberSortKeys: memberSortKeys ? memberSortKeys : []
                });
              }
            }
          }
        }
        break;
      }
    }
    return groupInfos;
  }

  get _familiesTargets(): Array<SendMessageTargetGroup> {
    return this.familiesTargets;
  }

  /**
   * ### 家族個別の宛先を格納する
   *
   * - 親コンポーネントから呼び出される
   */
  set _familiesTargets(familiesTargets: Array<SendMessageTargetGroup>) {
    this.familiesTargets = familiesTargets;
    return;
  }

  get _otherUserTargets(): Array<OtherUserGroupTypeGroup> {
    return this.otherUserTargets;
  }

  /**
   * ### 施設関係者個別の宛先を格納する
   *
   * - 親コンポーネントから呼び出される
   */
  set _otherUserTargets(otherUserTargets: Array<OtherUserGroupTypeGroup>) {
    this.otherUserTargets = otherUserTargets;
    return;
  }

  get _groupTargets(): GroupModel {
    return this.groupTargets;
  }

  /**
   * ### 宛先グループの宛先を格納する
   *
   * - 親コンポーネントから呼び出される
   */
  set _groupTargets(groupTargets: GroupModel) {
    this.groupTargets = groupTargets;
    return;
  }
}

export interface GroupModel {
  familyGroups: Array<SendMessageGroupTargetModel>;
  otherUserGroupMemberGroups: Array<SendMessageGroupTargetModel>;
  residentGroups: Array<ResidentGroupTargetModel>;
  otherUserGroups: Array<OtherUserGroupTargetModel>;
}
export interface SendMessageGroupTargetModel extends SendMessageGroupsGetModel {
  /** モーダルの表示可否 */
  show: boolean;
  /** 宛先対象か否か */
  isTarget: boolean;
  /** グループメンバー数 */
  memberCount: number;
}
export interface ResidentGroupTargetModel extends ResidentGroupsGetModel {
  /** モーダルの表示可否 */
  show: boolean;
  /** 宛先対象か否か */
  isTarget: boolean;
  /** グループメンバー数 */
  memberCount: number;
  /** key */
  key: string;
}
export interface OtherUserGroupTargetModel extends OtherUserGroup {
  /** モーダルの表示可否 */
  show: boolean;
  /** 宛先対象か否か */
  isTarget: boolean;
  /** グループメンバー数 */
  memberCount: number;
  /** key */
  key: string;
}
</script>
<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";
.send-message-targets-input {
  .send-targets {
    max-width: 600px;

    .send-target-select-btn {
      margin: 0 8px 4px 0;
    }
    .send-target {
      display: inline-block;
      padding: 7px;
      @include font(16px);
      margin: 0 8px 4px 0;
      border-radius: 3px;
      transition: all 0.2s;
      border: 1px solid $form-border-color;

      .target-name {
        margin: 0 8px 0 0 !important;
      }
      .delete-btn {
        margin: 0 !important;
        font-size: 14px !important;
      }
    }
  }
}
</style>
